import { ApplicationRef, inject, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { PrismSnackbarConfig, PrismSnackbarRef, PrismSnackbarService } from '@shure/prism-angular-components';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

export enum CloseTextOption {
	Ok = 'OK',
	Dismiss = 'Dismiss',
	Custom = 'Custom'
}

export enum MessageType {
	Info,
	Warning,
	Error
}

@shUseTranslation('scHtmlSnackbar', /* istanbul ignore next */ (lang) => import(`../i18n/${lang}`))
@Injectable({ providedIn: 'root' })
export class SnackbarService {
	private readonly snackbar = inject(PrismSnackbarService);
	private readonly transloco = inject(TranslocoService);
	private readonly injector = inject(Injector);

	/**
	 * Open snackbar with custom message, custom dismiss text and optional custom configs.
	 */
	public open(
		message: string,
		closeText: CloseTextOption = CloseTextOption.Ok,
		messageType: MessageType = MessageType.Info,
		configOptions?: PrismSnackbarConfig,
		customDismissText?: string
	): PrismSnackbarRef<unknown> {
		let actionText: string;
		let icon: string | undefined;
		let iconColor: string | undefined;

		switch (closeText) {
			case CloseTextOption.Dismiss:
				actionText = this.transloco.translate('scHtmlSnackbar.dismiss');
				break;
			case CloseTextOption.Custom:
				actionText = customDismissText ?? '';
				break;
			case CloseTextOption.Ok:
			default:
				actionText = this.transloco.translate('scHtmlSnackbar.ok');
				break;
		}

		switch (messageType) {
			case MessageType.Warning:
				icon = 'warning';
				iconColor = 'var(--prism-color-theme-state-ok)';
				break;
			case MessageType.Error:
				icon = 'error';
				iconColor = 'var(--prism-color-theme-state-bad)';
				break;
		}

		return this.snackbar.open({
			message: this.getDisplayValue(message),
			actionText,
			icon,
			iconColor,
			duration: configOptions?.duration
		});
	}

	public closeSnackBar(): void {
		this.snackbar.getOpenSnackbar()?.dismiss();
	}

	private getDisplayValue(value: string): string {
		const isKeyPattern = value?.startsWith('scHtmlSnackbar');

		if (isKeyPattern) {
			return this.transloco.translate(value);
		}

		return value;
	}

	private getRootVcr(): ViewContainerRef {
		const appRef = this.injector.get(ApplicationRef);
		return appRef.components[0].injector.get(ViewContainerRef);
	}
}
